import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function AdminLogin() {
  const { login, token, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && token) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    "w-full rounded-xl border-2 border-nosh-border bg-white px-4 py-3 font-body text-nosh-ink placeholder:text-nosh-ink/40 outline-none transition-colors focus:border-nosh-green focus:ring-2 focus:ring-nosh-green/20";

  return (
    <div className="grid min-h-screen place-items-center bg-nosh-surface px-5">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md rounded-[1.5rem] border border-nosh-border bg-nosh-cream p-8 shadow-xl"
        data-testid="admin-login-card"
      >
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="grid place-items-center rounded-full bg-nosh-green p-1 shadow-md ring-1 ring-nosh-gold/40">
            <img src="/nosh-logo.png" alt="Nosh" className="h-16 w-16 object-contain" />
          </span>
          <h1 className="mt-4 font-display text-3xl font-bold text-nosh-ink">Admin Portal</h1>
          <p className="mt-1 font-body text-sm text-nosh-ink/60">Sign in to manage catering inquiries</p>
        </div>

        <form onSubmit={onSubmit} className="grid gap-4" data-testid="admin-login-form">
          <div>
            <label className="mb-1.5 block font-poster text-[11px] font-bold uppercase tracking-widest text-nosh-green">Email</label>
            <input data-testid="admin-email-input" type="email" className={field} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@noshcatering.com" autoComplete="username" />
          </div>
          <div>
            <label className="mb-1.5 block font-poster text-[11px] font-bold uppercase tracking-widest text-nosh-green">Password</label>
            <input data-testid="admin-password-input" type="password" className={field} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
          </div>

          {error && (
            <div data-testid="admin-login-error" className="rounded-lg border border-red-300 bg-red-50 px-4 py-2.5 font-body text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={submitting}
            className="mt-1 flex items-center justify-center gap-2.5 rounded-full bg-nosh-green px-7 py-3.5 font-poster text-sm font-bold uppercase tracking-wide text-nosh-cream transition-colors duration-300 hover:bg-nosh-ink disabled:opacity-70"
          >
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : <><Lock className="h-4 w-4" /> Sign in</>}
          </button>
        </form>

        <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 font-poster text-xs font-bold uppercase tracking-wide text-nosh-ink/50 transition-colors hover:text-nosh-green">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
      </motion.div>
    </div>
  );
}
